import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Send, CheckCircle2, Link2, Check, Users } from "lucide-react";
import { COUNTRIES } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Waitlist({ t, lang, count, onCount, onContact }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("player");
  const [country, setCountry] = useState("IT");
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError(t.waitlist.invalid);
      return;
    }
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        role,
        country,
        lang,
      });
      onCount(data.count);
      setResult(data.status);
      setStatus("done");
    } catch {
      setError(t.waitlist.error);
      setStatus("idle");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const done = status === "done";
  const already = result === "already_registered";

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative overflow-hidden border-t border-white/[0.06] bg-[#0A0D14] py-28 lg:py-36"
    >
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[50vh] w-[70vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00F562]/[0.07] blur-[130px]" />
      <div className="relative mx-auto max-w-2xl px-6 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F562]">
          {t.waitlist.overline}
        </p>
        <h2 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">
          {t.waitlist.title}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-400">
          {t.waitlist.sub}
        </p>

        <div className="relative mt-12 min-h-[220px]">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.form
                key="form"
                data-testid="waitlist-form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="rounded-2xl border border-white/[0.08] bg-[#0D111A]/90 p-6 backdrop-blur-md sm:p-8"
              >
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    data-testid="waitlist-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.waitlist.emailPlaceholder}
                    className="w-full flex-1 rounded-full border border-white/10 bg-[#06080C] px-5 py-4 text-sm text-white placeholder-slate-500 outline-none transition-colors duration-300 focus:border-[#00F562]/60"
                  />
                  <select
                    data-testid="waitlist-role-select"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    aria-label={t.waitlist.roleLabel}
                    className="rounded-full border border-white/10 bg-[#06080C] px-5 py-4 text-sm text-slate-300 outline-none transition-colors duration-300 focus:border-[#00F562]/60"
                  >
                    {Object.entries(t.waitlist.roles).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-3">
                  <select
                    data-testid="waitlist-country-select"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    aria-label={t.waitlist.countryLabel}
                    className="w-full rounded-full border border-white/10 bg-[#06080C] px-5 py-4 text-sm text-slate-300 outline-none transition-colors duration-300 focus:border-[#00F562]/60"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c[lang] || c.en}
                      </option>
                    ))}
                  </select>
                </div>
                {error && (
                  <p
                    data-testid="waitlist-error-message"
                    className="mt-3 text-left text-xs text-red-400"
                  >
                    {error}
                  </p>
                )}
                <button
                  data-testid="waitlist-submit-button"
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-5 flex w-full items-center justify-center gap-3 rounded-full bg-[#00F562] px-8 py-4 text-sm font-extrabold uppercase tracking-widest text-[#06080C] transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,245,98,0.55)] disabled:opacity-60"
                >
                  <Send size={16} />
                  {status === "loading" ? t.waitlist.sending : t.waitlist.submit}
                </button>
                <div
                  data-testid="waitlist-counter"
                  className="mt-5 flex items-center justify-center gap-2 font-mono text-xs text-slate-500"
                >
                  <Users size={13} className="text-[#00F562]" />
                  <span className="font-bold text-[#00F562]">
                    {count === null ? "—" : count}
                  </span>
                  {t.waitlist.counterLabel}
                </div>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                data-testid="waitlist-success-card"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="rounded-2xl border border-[#00F562]/30 bg-[#0D111A]/90 p-8 backdrop-blur-md"
              >
                <CheckCircle2 size={40} className="mx-auto text-[#00F562]" />
                <h3 className="mt-4 font-display text-xl font-bold uppercase">
                  {already ? t.waitlist.alreadyTitle : t.waitlist.successTitle}
                </h3>
                <p className="mt-2 text-sm text-slate-400">
                  {already ? t.waitlist.alreadyBody : t.waitlist.successBody}
                </p>
                <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button
                    data-testid="waitlist-copy-link-button"
                    onClick={copyLink}
                    className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-xs font-bold uppercase tracking-widest text-slate-200 transition-colors duration-300 hover:border-[#00F562]/60 hover:text-[#00F562]"
                  >
                    {copied ? <Check size={14} /> : <Link2 size={14} />}
                    {copied ? t.waitlist.copied : t.waitlist.copyLink}
                  </button>
                  <button
                    data-testid="waitlist-contact-button"
                    type="button"
                    onClick={onContact}
                    className="flex items-center gap-2 rounded-full bg-[#00F562] px-5 py-3 text-xs font-extrabold uppercase tracking-widest text-[#06080C] transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,245,98,0.5)]"
                  >
                    <Mail size={14} />
                    {t.chat.floatingLabel}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-10 text-xs uppercase tracking-widest text-slate-500">
          {t.waitlist.contactAlt}{" "}
          <button
            data-testid="waitlist-direct-contact-button"
            type="button"
            onClick={onContact}
            className="font-mono text-[#00F562] underline-offset-4 hover:underline"
          >
            {t.chat.floatingLabel}
          </button>
        </p>
      </div>
    </section>
  );
}
